import { Grid } from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { TreeItem, TreeView } from '@material-ui/lab';
import React, { ReactElement, useState } from 'react';
import { DocumentNode } from './document-viewer/document-utils';
import { rootStyle } from './theme';

export const defaultHorizontalContainerStyles = {
  width: '100%',
  flexGrow: 1,
};

export const defaultVerticalContainerStyles = {
  flexGrow: 1,
};

const defaultGridItemStyles = {};

export interface LayoutProps {
  children?: ReactElement | ReactElement[];
  containerStyle?: object;
  itemStyle?: object;
  spacing?: number;
  align?: 'flex-start' | 'center' | 'flex-end';
}

export function HBox({
  containerStyle,
  itemStyle,
  spacing,
  children,
  align,
}: LayoutProps) {
  spacing = !isNaN(spacing) ? spacing : 2;
  children = (
    Array.isArray(children) ? children : [children]
  ) as ReactElement[];
  const classes = rootStyle(defaultHorizontalContainerStyles, containerStyle)();
  const gridItem = rootStyle(defaultGridItemStyles, itemStyle)();
  const alignItems = align || 'center';
  return (
    <Grid
      container
      direction="row"
      wrap="nowrap"
      spacing={spacing as any}
      alignItems={alignItems}
      justify={alignItems === 'flex-end' ? alignItems : null}
      className={classes.root}
      id="hbox"
    >
      {children.map((el, i) => (
        <Grid item key={i.toString()} className={gridItem.root}>
          {el}
        </Grid>
      ))}
    </Grid>
  );
}

export function VBox({
  containerStyle,
  itemStyle,
  spacing,
  children,
  align,
}: LayoutProps) {
  spacing = !isNaN(spacing) ? spacing : 2;
  children = (
    Array.isArray(children) ? children : [children]
  ) as ReactElement[];
  const classes = rootStyle(defaultVerticalContainerStyles, containerStyle)();
  const gridItem = rootStyle(defaultGridItemStyles, itemStyle)();
  const alignItems = align || 'center';
  return (
    <Grid
      container
      direction="column"
      spacing={spacing as any}
      alignItems={alignItems}
      className={classes.root}
      id="vbox"
    >
      {children.map((el, i) => (
        <Grid item key={i.toString()} className={gridItem.root}>
          {el}
        </Grid>
      ))}
    </Grid>
  );
}

const defaultTreeStyles = {
  root: {
    height: 216,
    flexGrow: 1,
    maxWidth: 400,
  },
};

export interface TreeProps<T = any> {
  id: (data: T) => string;
  label: (data: T) => string;
  children: (data: T) => T[];
  data: T;
  selectedIds?: string[];
  onSelected?: (data: T) => void;
  style?: object;
  expanded?: any[];
  title?: string;
  firstDocument?: (a: DocumentNode[]) => DocumentNode;
}

export function Tree<T = any>(props: TreeProps<T>) {
  const { style, data, id, label, children, selectedIds } = props;
  const classes = rootStyle(defaultTreeStyles, style)();
  const [expanded, setExpanded] = useState(props.expanded || []);
  const [selected, setSelected] = useState(selectedIds || []);
  const map = new Map<string, T>();
  if (!data) {
    return <>No Data</>;
  }

  const onToggle = (_event, nodeIds) => {
    setExpanded(nodeIds);
  };

  const onSelect = (_event, id) => {
    setSelected(id);

    if (props.onSelected) {
      props.onSelected(map.get(id as string));
    }
  };

  const renderTree = (n: T) => {
    if (Array.isArray(n)) {
      return (n as any).map((v) => renderTree(v));
    }
    const key = id(n) + '';
    map.set(key, n);
    const nodes = children(n);
    return (
      <TreeItem key={key} nodeId={key} label={label(n)}>
        {Array.isArray(nodes) ? nodes.map((v) => renderTree(v)) : null}
      </TreeItem>
    );
  };

  return (
    <TreeView
      className={classes.root}
      defaultCollapseIcon={<ExpandMoreIcon />}
      defaultExpanded={['root']}
      defaultExpandIcon={<ChevronRightIcon />}
      expanded={expanded}
      selected={selected}
      onNodeToggle={onToggle}
      onNodeSelect={onSelect}
    >
      {renderTree(data)}
    </TreeView>
  );
}
